import React, { useCallback, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  EChartsOption,
  SeriesOption,
  GraphicComponentOption,
  ECElementEvent,
  EChartsType,
  LineSeriesOption,
} from 'echarts';
import {
  CallbackDataParams,
  TooltipFormatterCallback,
  TopLevelFormatterParams,
  YAXisOption,
} from 'echarts/types/dist/shared';
import type { Element, ElementProps, Group, Line } from 'zrender';
import { Battery4chart } from 'src/models';
import { useLanguage } from 'src/hooks';
import { useDebounce } from 'src/helper';
import styles from '../headsetBatteryLogsStyles.module.scss';

export enum ChartType {
  bar = 'bar',
  scatter = 'scatter',
  line = 'line',
  lineWithArea = 'lineWithArea',
}

type YAXisOptionV6 = YAXisOption & {
  nameTextStyle?: YAXisOption['nameTextStyle'] & { show?: boolean };
};

type AxisParams = CallbackDataParams & {
  axisValue: string;
  axisValueLabel?: string;
};

const xAxisField = 'timestamp' as const;
type XAxisField = typeof xAxisField;

const baseYAxisField = 'batteryChargeLevel' as const;

const yAxisFields = [
  baseYAxisField,
  'isCharging',
  'batteryVoltage',
  'chargeCounter',
  'dischargeSpeed',
  'dischargeSpeedMah',
  'batteryTemperature',
  'batteryTemperatureMax',
] as const;
type YAxisField = (typeof yAxisFields)[number];

type OriginalGraphData = Record<XAxisField, string[]> &
  Record<YAxisField, number[]>;

type AdditionalSeriesData = { max: number; k: number };
type AdditionalGraphData = Record<
  YAxisField,
  { data: number[] } & AdditionalSeriesData
>;
type GraphData = Record<XAxisField, string[]> & AdditionalGraphData;

const axisColors: Record<YAxisField, string> = {
  batteryChargeLevel: '#5070dd',
  isCharging: '#f85050',
  batteryVoltage: '#e65ad3',
  chargeCounter: '#13df0c',
  dischargeSpeed: '#dfe427',
  dischargeSpeedMah: '#e6a416',
  batteryTemperature: '#a61ecc',
  batteryTemperatureMax: '#d813df',
};

const initialLegendState: Record<YAxisField, boolean> = {
  batteryChargeLevel: true,
  isCharging: false,
  batteryVoltage: true,
  chargeCounter: true,
  dischargeSpeed: true,
  dischargeSpeedMah: true,
  batteryTemperature: false,
  batteryTemperatureMax: false,
};

const getYAxisLefts = (
  elems: Element<ElementProps>[],
): Partial<Record<YAxisField, number>> => {
  if (!elems || !Array.isArray(elems)) {
    return {};
  }

  const result: Partial<Record<YAxisField, number>> = {};

  return elems
    .filter((elem) => elem != null)
    .reduce<Partial<Record<YAxisField, number>>>((res, elem) => {
      if (elem.type === 'group') {
        const group = elem as unknown as Group;
        try {
          const children = group.children();
          return children.length ? { ...res, ...getYAxisLefts(children) } : res;
        } catch (e) {
          return res;
        }
      }

      if (elem.type === 'text') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const text: YAxisField = elem['__fullText'];
        if (text && yAxisFields.includes(text)) {
          try {
            const group = elem.parent as unknown as Group;
            const line = group
              .children()
              .find((el) => (el as Line).anid === 'line');

            if (line) {
              return {
                ...res,
                [text]: Math.trunc(line.getBoundingRect().x),
              };
            }
          } catch (e) {
            //
          }
        }
      }

      return res;
    }, result);
};

type BatteryLogsChartProps = {
  filteredLogs: Battery4chart[];
  chartType: ChartType;
};

export const BatteryLogsChart = ({
  filteredLogs,
  chartType,
}: BatteryLogsChartProps) => {
  const { t } = useLanguage();
  const chartRef = useRef<ReactECharts | null>(null);

  const axisDisplayNames = useMemo(
    () =>
      yAxisFields.reduce(
        (res, field) => {
          return {
            ...res,
            [field]: t(`page.headset.battery.tabs.axis.${field}`),
          };
        },
        {} as Record<YAxisField, string>,
      ),
    [t],
  );

  const [legendState, setLegendState] = useState(initialLegendState);

  const onLegendSelectChanged = useCallback((params: ECElementEvent) => {
    setLegendState(params.selected);
  }, []);

  const [yAxisLefts, setYAxisLefts] = useState<
    Partial<Record<YAxisField, number>>
  >({});

  const thrOnRendered = useCallback((instance: EChartsType) => {
    const roots = instance.getZr().storage.getRoots();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lefts = getYAxisLefts(roots as any[]);
    setYAxisLefts(() => lefts);
  }, []);

  const onRender = useDebounce(thrOnRendered, 200);

  const onRendered = useCallback(
    (params: unknown, instance: EChartsType) => {
      onRender(instance);
    },
    [onRender],
  );

  const graphData: GraphData = useMemo(() => {
    const emptyOriginalData = [xAxisField, ...yAxisFields].reduce(
      (res, field) => ({ ...res, [field]: [] }),
      {} as OriginalGraphData,
    );

    const originalData = filteredLogs.reduce(
      (res, logData) =>
        [xAxisField, ...yAxisFields].reduce(
          (res, field) => ({
            ...res,
            [field]: [...res[field], logData[field]],
          }),
          res,
        ),
      emptyOriginalData,
    );

    const baseFieldMax = Math.max(...originalData[baseYAxisField]);

    const additionalData = yAxisFields.reduce(
      (res, field) => ({
        ...res,
        [field]: {
          data: originalData[field],
          max: Math.max(...originalData[field]),
          k:
            field === baseYAxisField
              ? 1
              : baseFieldMax / Math.max(...originalData[field]),
        },
      }),
      {} as AdditionalGraphData,
    );

    return {
      [xAxisField]: originalData[xAxisField],
      ...additionalData,
    };
  }, [filteredLogs]);

  const tooltipFormatter: TooltipFormatterCallback<TopLevelFormatterParams> =
    useCallback(
      (params) => {
        if (!Array.isArray(params)) return '';

        const date = (params[0] as AxisParams).axisValue;
        const formattedDate = new Date(date).toLocaleDateString('ru-RU', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        });

        let result = `<strong>${formattedDate}</strong><br/>`;

        params.forEach((param) => {
          if (
            param.seriesIndex !== undefined &&
            yAxisFields[param.seriesIndex] !== 'isCharging'
          ) {
            const fieldName = yAxisFields[param.seriesIndex];
            const realValue = graphData[fieldName].data[param.dataIndex];
            let formattedValue = new Intl.NumberFormat().format(realValue);

            switch (fieldName) {
              case 'chargeCounter':
              case 'dischargeSpeed':
              case 'dischargeSpeedMah': {
                if (!realValue) {
                  formattedValue = '---';
                }
              }
            }

            const displayName = axisDisplayNames[fieldName];
            const chargeStyle =
              fieldName === baseYAxisField &&
              graphData['isCharging'].data[param.dataIndex]
                ? 'transform: translateY(5px); min-width: 10px; max-width: 10px; min-height: 10px; max-height: 10px; background: #f85050; border-radius: 5px;'
                : '';

            result += `<div style="width: 100%; display: flex; gap: 16px; justify-content: space-between;">
            <span>${param.marker} ${displayName}: </span>
            <span style="${chargeStyle}"></span>
            <b>${formattedValue}</b>
            </div>`;
          }
        });

        return result;
      },
      [graphData, axisDisplayNames],
    );

  const chartOption: EChartsOption = useMemo(() => {
    const series: SeriesOption[] = yAxisFields.map((fieldName) => {
      const seriesOption: SeriesOption = {
        name: fieldName,
        data:
          graphData[fieldName].data.map((value) =>
            Math.trunc(value * graphData[fieldName].k),
          ) || [],
        itemStyle: { color: axisColors[fieldName] },
        emphasis: {
          itemStyle: { color: axisColors[fieldName] },
          focus: 'series',
        },
        blur: { itemStyle: { opacity: 0.3 } },
      };

      switch (chartType) {
        case ChartType.bar: {
          seriesOption.type = 'bar';
          break;
        }
        case ChartType.scatter: {
          seriesOption.type = 'scatter';
          break;
        }
        case ChartType.line: {
          seriesOption.type = 'line';
          (seriesOption as LineSeriesOption).showSymbol = false;
          break;
        }
        case ChartType.lineWithArea: {
          seriesOption.type = 'line';
          (seriesOption as LineSeriesOption).showSymbol = false;
          (seriesOption as LineSeriesOption).areaStyle = {
            color: axisColors[fieldName],
            opacity: 0.1,
          };
        }
      }

      return seriesOption;
    });

    let extraAxisCounter = 0;
    const yAxisConfigs = yAxisFields.reduce((res, fieldName) => {
      if (!legendState[fieldName] && fieldName !== baseYAxisField) {
        return res;
      }

      const yAx: YAXisOptionV6 = {
        type: 'value',
        name: fieldName,
        min: 0,
        max: graphData[fieldName].max * 1.1,
        alignTicks: true,
        splitLine: { show: true },
        axisTick: { show: true },
        axisLine: {
          show: true,
          lineStyle: { color: axisColors[fieldName] },
        },
        axisLabel: {
          formatter: (value: number) => {
            return new Intl.NumberFormat('en', {
              notation: 'compact',
            }).format(value);
          },
          fontSize: 10,
        },
        nameTextStyle: {
          color: 'transparent',
          show: false,
        },
      };

      if (fieldName !== baseYAxisField) {
        yAx.offset = 20 + extraAxisCounter * 55;
        extraAxisCounter += 1;
        return [...res, yAx];
      }

      return [yAx, ...res];
    }, [] as YAXisOptionV6[]);

    return {
      grid: {
        show: true,
        left: 5,
        right: 5,
        bottom: 50,
        top: 30,
        borderWidth: 0,
      },
      tooltip: {
        trigger: 'axis',
        alwaysShowContent: false,
        borderWidth: 1,
        formatter: tooltipFormatter,
      },
      xAxis: {
        type: 'category',
        axisLabel: {
          formatter: (value: string) =>
            new Date(value).toLocaleString().split(',')[0],
        },
        data: graphData[xAxisField],
      },
      yAxis: yAxisConfigs,
      series,
      legend: {
        show: true,
        selectedMode: 'multiple',
        selected: legendState,
        bottom: 0,
        formatter: (name: string) => {
          return axisDisplayNames[name as YAxisField] || name;
        },
      },
    };
  }, [tooltipFormatter, graphData, legendState, axisDisplayNames, chartType]);

  const yAxisLabels = useMemo(
    () =>
      yAxisFields.reduce((res, fieldName) => {
        if (!legendState[fieldName] || fieldName === baseYAxisField) {
          return res;
        }

        let left = 0;
        switch (fieldName) {
          case 'isCharging': {
            left = yAxisLefts.isCharging ?? 0;
            break;
          }
          case 'batteryVoltage': {
            left = yAxisLefts.batteryVoltage ?? 0;
            break;
          }
          case 'chargeCounter': {
            left = yAxisLefts.chargeCounter ?? 0;
            break;
          }
          case 'dischargeSpeed': {
            left = yAxisLefts.dischargeSpeed ?? 0;
            break;
          }
          case 'dischargeSpeedMah': {
            left = yAxisLefts.dischargeSpeedMah ?? 0;
            break;
          }
          case 'batteryTemperature': {
            left = yAxisLefts.batteryTemperature ?? 0;
            break;
          }
          case 'batteryTemperatureMax': {
            left = yAxisLefts.batteryTemperatureMax ?? 0;
          }
        }

        if (left === 0) {
          return res;
        }

        return [
          ...res,
          {
            type: 'text',
            left: left - 11,
            top: 30,
            rotation: Math.PI / 2,
            style: {
              text: axisDisplayNames[fieldName],
              fill: axisColors[fieldName],
              fontSize: 12,
              fontWeight: 'bold',
            },
            z: 100,
          },
        ];
      }, [] as GraphicComponentOption[]),
    [
      legendState,
      axisDisplayNames,
      yAxisLefts.isCharging,
      yAxisLefts.batteryVoltage,
      yAxisLefts.chargeCounter,
      yAxisLefts.dischargeSpeed,
      yAxisLefts.dischargeSpeedMah,
      yAxisLefts.batteryTemperature,
      yAxisLefts.batteryTemperatureMax,
    ],
  );

  const fullChartOption: EChartsOption = useMemo(
    () => ({ ...chartOption, graphic: yAxisLabels }),
    [chartOption, yAxisLabels],
  );

  const chartStyle = useMemo(() => ({ width: '100%', height: '600px' }), []);

  return (
    <ReactECharts
      ref={chartRef}
      option={fullChartOption}
      style={chartStyle}
      className={styles.chart}
      notMerge={true}
      onEvents={{
        legendselectchanged: onLegendSelectChanged,
        rendered: onRendered,
      }}
    />
  );
};
